/*
 * Display colour of a measured sample.
 *
 * Every colour painted in the UI (swatches, patch grids, plot markers) is
 * derived from the sample's measured CIELAB value - never from the camera RGB
 * reading (a raw sensor value, not a display colour) and not from a separate
 * CIEXYZ conversion, so the same sample looks the same everywhere.
 * helper/displayColorSource.test.js scans the sources for violations.
 */

const triple = (value, keys) => {
    if (!value) return null;
    const raw = Array.isArray(value) ? value.slice(0, 3) : keys.map((key) => value?.[key]);
    // null / "" are missing values, not 0 (Number(null) is 0).
    if (raw.length !== 3 || raw.some((channel) => channel === null || channel === undefined || channel === "")) return null;
    const numbers = raw.map(Number);
    return numbers.every(Number.isFinite) ? numbers : null;
};

const clampChannel = (channel) => Math.max(0, Math.min(255, Math.round(channel)));

/** sRGB companding of a linear channel in 0..1. */
const gamma = (value) => (value > 0.0031308 ? (1.055 * (value ** (1 / 2.4))) - 0.055 : 12.92 * value);

/**
 * CIELAB (D65) to a CSS sRGB string. Accepts ``[L, a, b]`` or ``{l, a, b}``;
 * null when a channel is missing or not a number.
 */
export const labToDisplayRgb = (lab) => {
    const channels = triple(lab, ["l", "a", "b"]);
    if (!channels) return null;
    const [L, a, b] = channels;
    const fy = (L + 16) / 116;
    const fx = (a / 500) + fy;
    const fz = fy - (b / 200);
    const pivotInv = (value) => {
        const cube = value * value * value;
        return cube > 0.008856 ? cube : (value - 16 / 116) / 7.787;
    };
    const xr = 0.95047 * pivotInv(fx);
    const yr = pivotInv(fy);
    const zr = 1.08883 * pivotInv(fz);
    const r = (xr * 3.2406) + (yr * -1.5372) + (zr * -0.4986);
    const g = (xr * -0.9689) + (yr * 1.8758) + (zr * 0.0415);
    const bl = (xr * 0.0557) + (yr * -0.2040) + (zr * 1.0570);
    const [cr, cg, cb] = [gamma(r), gamma(g), gamma(bl)].map((channel) => clampChannel(channel * 255));
    return `rgb(${cr}, ${cg}, ${cb})`;
};

/** Display colour of a performance record (``l_star`` / ``a_star`` / ``b_star``); null when unmeasured. */
export const labCss = (performance) => (
    performance ? labToDisplayRgb([performance.l_star, performance.a_star, performance.b_star]) : null
);

/**
 * ``[r, g, b]`` (0..255) to a CSS string. Only for display triples the
 * backend already derived from CIELAB (the ``rgb`` of repeatability cells,
 * palette picks, ...).
 */
export const rgbString = (rgb) => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
